import { useCallback, useRef, useState } from 'react';
import { usingStub } from '../ar';
import { releaseMicrophone, type Clip } from '../capture';
import type { AppConfig } from '../config';
import { diagnostics, noteEvent, patchDiagnostics } from '../lib/diagnostics';
import { detectBrowserContext } from '../lib/inAppBrowser';
import { createObjectUrl, revokeAllObjectUrls, revokeObjectUrl } from '../lib/objectUrls';
import { newSessionId } from '../lib/session';
import { newShareCode } from '../lib/shareCode';
import { getAttribution } from '../lib/utm';
import { logSession } from '../upload';
import type { Step } from './types';

/**
 * Shared session state for both flows.
 *
 * Owns the things that outlive a single screen: the session id, the mic stream,
 * the optional permission camera stream (Camera Kit), the captured clip and its
 * object URL, and the retake count. The AR module and the MediaRecorder are
 * owned by the capture stage instead, so unmounting that screen is what tears
 * them down — which is also what an idle reset does.
 */

export interface Activation {
  step: Step;
  goTo: (step: Step) => void;

  sessionId: string;
  /** Short code a guest can copy down to retrieve the clip without a number. */
  shareCode: string;
  retakes: number;

  micStream: MediaStream | null;
  micGranted: boolean;
  setMic: (stream: MediaStream | null, granted: boolean) => void;

  /** Live camera kept from the permission step for Camera Kit reuse. */
  cameraStream: MediaStream | null;
  setCameraStream: (stream: MediaStream | null) => void;

  clip: Clip | null;
  clipUrl: string | null;
  acceptClip: (clip: Clip | null) => void;
  /** Retake: drop the clip and go back to the live camera. */
  retake: () => void;

  error: Error | null;
  fail: (err: Error) => void;

  /** Full teardown. Kiosk idle reset and web restart both land here. */
  resetSession: (toStep: Step) => void;
  /** Marks the session complete for reporting. */
  completeSession: () => void;
}

function releaseCamera(stream: MediaStream | null): void {
  stream?.getTracks().forEach((t) => t.stop());
}

export function useActivation(config: AppConfig, initialStep: Step): Activation {
  const [step, setStep] = useState<Step>(initialStep);
  const [sessionId, setSessionId] = useState<string>(() => {
    patchDiagnostics({ arSource: usingStub ? 'stub' : 'real' });
    diagnostics.set((p) => ({ ...p, sessionCount: p.sessionCount + 1 }));
    return newSessionId();
  });
  // One code per session, alongside the id. A retake keeps both — it is the same
  // guest and the same clip slot — and a reset issues new ones.
  const [shareCode, setShareCode] = useState<string>(() => newShareCode());
  const [retakes, setRetakes] = useState(0);
  const [clip, setClip] = useState<Clip | null>(null);
  const [clipUrl, setClipUrl] = useState<string | null>(null);
  const [micGranted, setMicGranted] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Refs, not state: streams are not render-relevant and the recorder needs
  // the current mic synchronously.
  const micStreamRef = useRef<MediaStream | null>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);

  const goTo = useCallback((next: Step) => {
    noteEvent(`step → ${next}`);
    setStep(next);
  }, []);

  const setMic = useCallback((stream: MediaStream | null, granted: boolean) => {
    micStreamRef.current = stream;
    setMicGranted(granted);
  }, []);

  const setCameraStream = useCallback((stream: MediaStream | null) => {
    cameraStreamRef.current = stream;
  }, []);

  const dropClip = useCallback(() => {
    setClipUrl((prev) => {
      revokeObjectUrl(prev);
      return null;
    });
    setClip(null);
  }, []);

  const acceptClip = useCallback(
    (next: Clip | null) => {
      dropClip();
      if (!next) return;
      setClip(next);
      setClipUrl(createObjectUrl(next.blob));
    },
    [dropClip],
  );

  const retake = useCallback(() => {
    dropClip();
    setRetakes((n) => n + 1);
    noteEvent('retake');
    goTo('capture');
  }, [dropClip, goTo]);

  const fail = useCallback(
    (err: Error) => {
      setError(err);
      goTo('error');
    },
    [goTo],
  );

  const completeSession = useCallback(() => {
    diagnostics.set((p) => ({ ...p, completedCount: p.completedCount + 1 }));
    logSession({
      session_id: sessionId,
      mode: config.mode,
      ar_source: usingStub ? 'stub' : 'real',
      mic_granted: micGranted,
      completed: true,
      retakes,
      clip_bytes: clip?.sizeBytes ?? null,
      clip_mime: clip?.mimeType ?? null,
      user_agent: navigator.userAgent,
      attribution: config.mode === 'web' ? getAttribution() : null,
      in_app_browser: detectBrowserContext().inApp,
    });
  }, [clip, config.mode, micGranted, retakes, sessionId]);

  const resetSession = useCallback(
    (toStep: Step) => {
      noteEvent('session reset');
      dropClip();
      revokeAllObjectUrls();
      releaseMicrophone(micStreamRef.current);
      micStreamRef.current = null;
      releaseCamera(cameraStreamRef.current);
      cameraStreamRef.current = null;
      setMicGranted(false);
      setError(null);
      setRetakes(0);
      setSessionId(newSessionId());
      setShareCode(newShareCode());
      diagnostics.set((p) => ({ ...p, sessionCount: p.sessionCount + 1 }));
      setStep(toStep);
    },
    [dropClip],
  );

  return {
    step,
    goTo,
    sessionId,
    shareCode,
    retakes,
    micStream: micStreamRef.current,
    micGranted,
    setMic,
    cameraStream: cameraStreamRef.current,
    setCameraStream,
    clip,
    clipUrl,
    acceptClip,
    retake,
    error,
    fail,
    resetSession,
    completeSession,
  };
}
